import React, { useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";

import Dialog from "../../../components/dialog/Dialog";
import { GetUsersByOrganisationId } from "../../../hooks/useUserData";
import { GetUserGroupsByOrganisationId } from "../../../hooks/useUserGroupdata";
import { CreateUserModal } from "../../../modals/customer/CreateUserModal";
import { UserEditModal } from "../customer/UserEditView";
import {
  Avatar,
  ClickableRow,
  CountDivider,
  EmptyState,
  INK_MUTED,
  ListShell,
  PrimaryButton,
  RowChevron,
  RowName,
  RowSkeleton,
  SearchBar,
  ToolbarRow,
  WarningBanner,
} from "./orgTabsKit";

const fullName = (u) =>
  [u.first_name, u.last_name].filter(Boolean).join(" ").trim() ||
  "Namnlös användare";

const MetaChip = ({ icon: Icon, label, tone }) => {
  const c =
    tone === "brand"
      ? { border: "rgba(138,17,37,0.3)", bg: "rgba(138,17,37,0.07)", fg: "#8a1125" }
      : { border: "rgba(16,50,74,0.3)", bg: "rgba(16,50,74,0.07)", fg: "#10324a" };
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.5,
        height: 28,
        maxWidth: 200,
        borderRadius: "999px",
        border: `1px solid ${c.border}`,
        background: c.bg,
        color: c.fg,
        fontSize: "12px",
        fontWeight: 700,
        fontFamily: '"DM Sans", sans-serif',
      }}
    >
      <Icon sx={{ fontSize: 14, flexShrink: 0 }} />
      <Box
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Box>
    </Box>
  );
};

export const OrgUsersTab = ({ organisationId, orgName }) => {
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editUserId, setEditUserId] = useState(null);

  const { data: usersData, isLoading, isError } =
    GetUsersByOrganisationId(organisationId);
  const { data: groupData, isLoading: groupsLoading } =
    GetUserGroupsByOrganisationId(organisationId);

  const users = useMemo(
    () => (Array.isArray(usersData?.data) ? usersData.data : []),
    [usersData]
  );

  const groupCount = Array.isArray(groupData?.data)
    ? groupData.data.length
    : 0;
  const noGroups = !groupsLoading && groupCount === 0;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      `${u.first_name || ""} ${u.last_name || ""} ${u.email || ""} ${
        u.phone || ""
      }`
        .toLowerCase()
        .includes(q)
    );
  }, [users, search]);

  return (
    <Box>
      <ToolbarRow>
        <SearchBar
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Sök namn, e-post eller telefon..."
        />
        <PrimaryButton
          icon={PersonAddRoundedIcon}
          label="Skapa användare"
          disabled={noGroups}
          onClick={() => setCreateOpen(true)}
        />
      </ToolbarRow>

      {noGroups && (
        <WarningBanner>
          Den här organisationen saknar grupper. Skapa en grupp under fliken
          Grupper först — en användare måste tilldelas en grupp.
        </WarningBanner>
      )}

      <CountDivider
        label={
          isLoading
            ? "Läser in användare"
            : `${filtered.length} ${
                filtered.length === 1 ? "användare" : "användare"
              }`
        }
      />

      {isLoading ? (
        <RowSkeleton rows={4} />
      ) : isError ? (
        <EmptyState
          icon={PersonRoundedIcon}
          title="Kunde inte hämta användare"
          hint="Något gick fel vid hämtningen. Kontrollera att du är inloggad som superadmin och försök igen."
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={PersonRoundedIcon}
          title={search.trim() ? "Inga träffar" : "Inga användare ännu"}
          hint={
            search.trim()
              ? "Prova en annan sökterm."
              : "Skapa en användare för att ge en person åtkomst i organisationen."
          }
        />
      ) : (
        <ListShell>
          {filtered.map((user) => {
            const groupName = user.user_group?.group_name;
            const facilityName = user.facility?.facility_name;
            return (
              <ClickableRow
                key={user.id}
                columns={{
                  xs: "auto 1fr auto",
                  md: "auto minmax(0,1.3fr) minmax(0,1fr) auto",
                }}
                onClick={() => setEditUserId(user.id)}
              >
                <Avatar name={fullName(user)} />
                <Box sx={{ minWidth: 0 }}>
                  <RowName>{fullName(user)}</RowName>
                  <Typography
                    sx={{
                      fontSize: "12.5px",
                      color: INK_MUTED,
                      mt: 0.25,
                      fontFamily: '"DM Sans", sans-serif',
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {user.email || "Ingen e-post"}
                  </Typography>
                </Box>
                <Stack
                  direction="row"
                  spacing={0.75}
                  sx={{ display: { xs: "none", md: "flex" }, minWidth: 0 }}
                >
                  {groupName ? (
                    <MetaChip
                      icon={GroupsRoundedIcon}
                      label={groupName}
                      tone="brand"
                    />
                  ) : null}
                  {facilityName ? (
                    <MetaChip
                      icon={ApartmentRoundedIcon}
                      label={facilityName}
                      tone="navy"
                    />
                  ) : null}
                </Stack>
                <RowChevron />
              </ClickableRow>
            );
          })}
        </ListShell>
      )}

      <Dialog
        title={`Skapa användare · ${orgName}`}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      >
        <CreateUserModal
          customerid={organisationId}
          showRoleSelect
          closeCreateModal={() => setCreateOpen(false)}
        />
      </Dialog>

      {editUserId && (
        <Dialog
          title="Redigera användare"
          open={!!editUserId}
          onClose={() => setEditUserId(null)}
        >
          <UserEditModal
            userId={editUserId}
            onClose={() => setEditUserId(null)}
            showRoleSelect
          />
        </Dialog>
      )}
    </Box>
  );
};

export default OrgUsersTab;
